import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import DatePicker from 'react-datepicker';
import { MenuButton } from '../menu-button/menu-button';
import { MenuItem } from '../menu-button/menu-item';
import { cmxDateTime, objectUtils } from '@codametrix/ui-common';
import './styles/index.scss';
const { isEmpty } = objectUtils;
/**
 * Fetches the initial label for a field based on the cell key, case edits, and value.
 * For billing_location fields, it attempts to find a mapped label from the case edits.
 *
 * @param cellKey - The key identifier for the cell
 * @param caseEdits - The current state of case edits
 * @param value - The current value of the field
 * @returns The label to display, or the original value if no mapping is found
 */
const fetchInitialLabel = (
  cellKey: string,
  caseEdits: CMxCommonApp.CaseEditsState,
  value: string
) => {
  if (
    cellKey === 'billing_location' &&
    !isEmpty(caseEdits) &&
    Object.keys(caseEdits).includes(cellKey)
  ) {
    const { mappedValues } = caseEdits?.[cellKey];
    const label = mappedValues?.find(
      (map: { label: string; value: string }) => {
        return map?.value === value;
      }
    )?.label;
    return label || value;
  }
  return value;
};

export type FieldEditorProps = {
  cellKey: string;
  label: string;
  value: React.ReactNode | string;
  fieldType?: string;
  onChange: (val: any) => void;
  onSubmit: (val: any) => void;
  fieldEditorInfo: CMxAPI.FieldEditorInfo;
} & React.HTMLAttributes<HTMLDivElement>;

const FieldEditor: React.FC<FieldEditorProps> = (props: FieldEditorProps) => {
  const defaultClassname = 'cmx-col-4 cmx-col-md-12';
  const {
    cellKey,
    label,
    value,
    fieldType,
    onChange,
    onSubmit,
    fieldEditorInfo,
    className = defaultClassname,
    ...spreadable
  } = props;
  const classes = classNames(
    `${cellKey}-editor`,
    'field-editor',
    'encounter-info',
    className
  );

  const { caseEdits } = fieldEditorInfo;

  let updatedValue = value;
  let menuSelect: React.ReactNode;

  if (caseEdits && Object.keys(caseEdits).includes(cellKey)) {
    const { newValue, initialValue } = caseEdits[cellKey];

    updatedValue = newValue?.length > 0 ? newValue : initialValue ?? 'N/A';
    // setSelected(updatedValue.toString());

    if (fieldType === 'dictionary') {
      const { options } = caseEdits[cellKey];

      menuSelect = options.map((option: string, index) => {
        return (
          <MenuItem
            key={`${cellKey}-${option}-${index}`}
            onSelect={e => {
              setSelected(e.currentTarget.innerText);
              onChange(e.currentTarget.innerText);
            }}>
            <div>{option}</div>
          </MenuItem>
        );
      });
    }
  }

  if (fieldType === 'date' && updatedValue !== 'N/A') {
    updatedValue = cmxDateTime.format(
      updatedValue.toString(),
      cmxDateTime.FORMATS.DATE
    );
  }

  const [selected, setSelected] = useState(
    fetchInitialLabel(cellKey, caseEdits, value.toString())
  );

  React.useEffect(() => {
    setSelected(fetchInitialLabel(cellKey, caseEdits, value.toString()));
  }, [value]);

  return (
    //@ts-ignore
    <div {...spreadable} className={classes} data-testid="field-editor">
      {fieldType === 'date' && (
        <div
          className="datepicker-qa date-field-editor"
          data-testid="field-editor-datepicker">
          <DatePicker
            key={updatedValue.toString()}
            selected={
              selected !== 'N/A' || null || undefined
                ? new Date(`${selected} 00:00`)
                : null
            }
            placeholderText={'No Date Selected'}
            onChange={(e: Date | null) => {
              const newTime = e ? e.toISOString().split('T')[0] : 'N/A';
              setSelected(newTime);
              onChange(newTime);
            }}
          />
        </div>
      )}
      {fieldType === 'dictionary' && (
        <div
          className="dictionary-menu-button tooltip"
          data-tooltip={selected}
          data-testid="field-editor-dictionary">
          <MenuButton
            key={updatedValue.toString()}
            label={selected !== 'N/A' ? `${selected}` : 'No Selection'}>
            {menuSelect}
          </MenuButton>
        </div>
      )}
    </div>
  );
};
FieldEditor.displayName = 'FieldEditor';

export { FieldEditor };
